import { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AppContext } from '@prisjakt/ui-app-context';

const makeTag = (tagName, attributes = {}, properties = {}) => {
  const element = document.createElement(tagName);
  Object.keys(attributes).forEach(attrName => {
    element.setAttribute(attrName, attributes[attrName]);
  });
  Object.keys(properties).forEach(propName => {
    element[propName] = properties[propName];
  });
  return element;
};

const makeOpenGraphTags = (openGraphEntries = []) =>
  openGraphEntries.map(openGraphEntry =>
    makeTag('meta', {
      property: `og:${openGraphEntry.key}`,
      content: openGraphEntry.content,
    })
  );

const makeLinkedDataTag = content =>
  content &&
  makeTag(
    'script',
    { type: 'application/ld+json' },
    { innerHTML: JSON.stringify(content) }
  );

const makeBreadcrumbDataTags = (market, breadcrumb) =>
  makeLinkedDataTag({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumb.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@id': market.domain + item.pathName,
        name: item.name,
      },
    })),
  });

const makeStructuredDataTags = (market, breadcrumb, structuredData) => {
  let structuredDataArray;

  if (!structuredData) {
    structuredDataArray = [];
  } else {
    structuredDataArray = !Array.isArray(structuredData)
      ? [structuredData]
      : structuredData;
  }

  return [
    breadcrumb && makeBreadcrumbDataTags(market, breadcrumb),
    ...structuredDataArray.map(makeLinkedDataTag),
  ].filter(Boolean);
};

const makeAndroidTag = (market, android) =>
  makeTag('link', {
    rel: 'alternate',
    href: `android-app://${market.android}/https/${market.host}${android}`,
  });

const makeIosTag = (market, ios) =>
  makeTag('meta', {
    name: 'apple-itunes-app',
    content: `app-id=${market.ios}, app-argument=https://${market.host}${ios}`,
  });

const makeHrefLangTags = (hrefLang = []) =>
  hrefLang.map(({ locale, url }) =>
    makeTag('link', {
      rel: 'alternate',
      hrefLang: locale,
      href: url,
    })
  );

function MetaTags(props) {
  const {
    routeId,
    locationKey,
    metaTags: {
      title,
      breadcrumb,
      description,
      thumbnail,
      canonical,
      structuredData,
      openGraph = [],
      android,
      ios,
      hrefLang = [],
      prev,
      next,
      noIndex,
      amp,
      botify = {},
    },
  } = props;

  const { market } = useContext(AppContext);

  useEffect(() => {
    if (!IS_BROWSER || (routeId === 'initial' && locationKey === undefined)) {
      // For initial request, do not mess with the meta tags sent from server
      return;
    }

    for (const metaTagElement of document.head.querySelectorAll(
      '[data-route-id]'
    )) {
      metaTagElement.parentNode.removeChild(metaTagElement);
    }

    const elements = [
      title && makeTag('title', {}, { innerText: title }),
      ...makeOpenGraphTags(openGraph),
      amp && makeTag('link', { rel: 'amphtml', href: amp }),
      noIndex && makeTag('meta', { name: 'robots', content: 'noindex' }),
      description &&
        makeTag('meta', { name: 'description', content: description }),
      thumbnail && makeTag('meta', { name: 'thumbnail', content: thumbnail }),
      canonical && makeTag('link', { rel: 'canonical', href: canonical }),
      prev && makeTag('link', { rel: 'prev', href: prev }),
      next && makeTag('link', { rel: 'next', href: next }),
      market.android && android && makeAndroidTag(market, android),
      market.ios && ios && makeIosTag(market, ios),
      ...makeHrefLangTags(hrefLang),
      ...makeStructuredDataTags(market, breadcrumb, structuredData),
      botify && makeTag('meta', { ...botify, name: 'botify' }),
    ].filter(Boolean);

    elements.forEach(element => {
      element.setAttribute('data-route-id', routeId);
      document.head.appendChild(element);
    });
  }, [routeId, locationKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

MetaTags.propTypes = {
  metaTags: PropTypes.object.isRequired,
  routeId: PropTypes.string.isRequired,
  locationKey: PropTypes.string,
};

export default MetaTags;
